import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';

interface Warehouse {
  id: number;
  warehouseCode: string;
  warehouseName: string;
  warehouseType: string;
  address?: string;
  managerName?: string;
  branchId?: number;
  branchName?: string;
  isActive: boolean;
  createdAt: string;
}

interface Branch {
  id: number;
  branchName: string;
}

const Warehouses: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadWarehouses();
    loadBranches();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/warehouses');
      setWarehouses(data);
    } catch (error) {
      message.error('Depolar yüklenirken hata oluştu');
      console.error('Warehouses load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(data.filter((b: any) => b.isActive));
    } catch (error) {
      console.error('Branches load error:', error);
    }
  };

  const handleAdd = () => {
    setEditingWarehouse(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    form.setFieldsValue(warehouse);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/warehouses/${id}`);
      message.success('Depo silindi');
      loadWarehouses();
    } catch (error) {
      message.error('Depo silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await api.patch(`/warehouses/${id}/toggle-active`);
      message.success(`Depo ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      loadWarehouses();
    } catch (error) {
      message.error('Durum değiştirilirken hata oluştu');
      console.error('Toggle active error:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (editingWarehouse) {
        await api.put(`/warehouses/${editingWarehouse.id}`, values);
        message.success('Depo güncellendi');
      } else {
        await api.post('/warehouses', values);
        message.success('Depo eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadWarehouses();
    } catch (error) {
      message.error('Depo kaydedilirken hata oluştu');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWarehouseTypeColor = (type: string) => {
    switch (type) {
      case 'MAIN': return 'blue';
      case 'SUB': return 'green';
      case 'VIRTUAL': return 'orange';
      default: return 'default';
    }
  };

  const getWarehouseTypeText = (type: string) => {
    switch (type) {
      case 'MAIN': return 'Ana Depo';
      case 'SUB': return 'Yan Depo';
      case 'VIRTUAL': return 'Sanal Depo';
      default: return type;
    }
  };

  const columns: ColumnsType<Warehouse> = [
    {
      title: 'Depo Kodu',
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
    },
    {
      title: 'Depo Adı',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
    },
    {
      title: 'Tip',
      dataIndex: 'warehouseType',
      key: 'warehouseType',
      render: (type: string) => (
        <Tag color={getWarehouseTypeColor(type)}>
          {getWarehouseTypeText(type)}
        </Tag>
      ),
    },
    {
      title: 'Şube',
      dataIndex: 'branchName',
      key: 'branchName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Müdür',
      dataIndex: 'managerName',
      key: 'managerName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => text || '-'
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record: Warehouse) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record.id, isActive)}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Bu depoyu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Depolar"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Depo
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={warehouses}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />

      <Modal
        title={editingWarehouse ? 'Depo Düzenle' : 'Yeni Depo'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="warehouseCode"
            label="Depo Kodu"
            rules={[{ required: true, message: 'Depo kodu giriniz' }]}
          >
            <Input placeholder="Örn: WH001" />
          </Form.Item>

          <Form.Item
            name="warehouseName"
            label="Depo Adı"
            rules={[{ required: true, message: 'Depo adı giriniz' }]}
          >
            <Input placeholder="Depo adı" />
          </Form.Item>

          <Form.Item
            name="warehouseType"
            label="Depo Tipi"
            rules={[{ required: true, message: 'Depo tipi seçiniz' }]}
          >
            <Select placeholder="Depo tipi seçiniz">
              <Select.Option value="MAIN">Ana Depo</Select.Option>
              <Select.Option value="SUB">Yan Depo</Select.Option>
              <Select.Option value="VIRTUAL">Sanal Depo</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="branchId"
            label="Şube (Opsiyonel)"
          >
            <Select placeholder="Şube seçin" allowClear>
              {branches.map(b => (
                <Select.Option key={b.id} value={b.id}>
                  {b.branchName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="managerName"
            label="Müdür Adı"
          >
            <Input placeholder="Müdür adı" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Adres"
          >
            <Input.TextArea placeholder="Adres bilgisi" rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingWarehouse ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Warehouses;
